import { useRef } from "react";
import { Typography, Box, Button } from "@mui/material";
import { MdPrint } from "react-icons/md";
import { HiOutlineReceiptPercent } from "react-icons/hi2";
import dayjs from "dayjs";
import Logo from "../../../../../assets/Logo";
import { useReactToPrint } from "react-to-print";
import FormDialog from "../../../../../components/FormDialog";

const statusStyle = (status) => {
  if (status === "success")
    return { bg: "#E8F5E9", color: "#2E7D32", label: "ناجحة" };
  if (status === "failed")
    return { bg: "#FFEBEE", color: "#C62828", label: "فاشلة" };
  if (status === "need_confirmation")
    return { bg: "#FFF8E1", color: "#B26A00", label: "بانتظار التأكيد" };
  return { bg: "#ECEFF1", color: "#455A64", label: status ?? "-" };
};

const DetailRow = ({ label, value, last }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2,
      py: 1.4,
      borderBottom: last ? "none" : "1px dashed #E0E0E0",
    }}
  >
    <Typography sx={{ color: "#78909C", fontSize: 14, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        color: "#263238",
        fontSize: 15,
        fontWeight: 700,
        textAlign: "left",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const TransactionPrintDialog = ({ open, transaction, onClose }) => {
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
    pageStyle: `
    @page {
      size: A4;
      margin: 12mm;
    }
    body {
      direction: rtl;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }`,
  });

  const t = transaction ?? {};
  const sc = statusStyle(t.status);

  return (
    <FormDialog
      open={open}
      maxWidth="sm"
      onClose={onClose}
      icon={<HiOutlineReceiptPercent />}
      title="إيصال معاملة مالية"
      subtitle={t.order_id ? `رقم الطلب #${t.order_id}` : undefined}
      extraActions={
        <Button
          variant="contained"
          startIcon={<MdPrint />}
          onClick={() => handlePrint()}
        >
          طباعة
        </Button>
      }
    >
        {/* Printable receipt */}
        <Box
          ref={printRef}
          sx={{
            bgcolor: "#fff",
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header band */}
          <Box
            sx={{
              bgcolor: "#fff",
              color: "#263238",
              px: 3,
              py: 2.5,
              borderBottom: "1px solid #ECEFF1",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3 }}
              >
                شركة دجلة للتجارة العامة
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#90A4AE" }}>
                والتسويق الالكتروني
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Logo style={{ width: 90, height: 60, objectFit: "contain" }} />
            </Box>
          </Box>

          {/* Amount hero */}
          <Box
            sx={{
              textAlign: "center",
              px: 3,
              pt: 3,
              pb: 2,
            }}
          >
            <Typography
              sx={{ color: "#90A4AE", fontSize: 13, fontWeight: 600, mb: 0.5 }}
            >
              المبلغ
            </Typography>
            <Typography
              sx={{
                color: "#263238",
                fontSize: 34,
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {t.amount != null ? parseInt(t.amount)?.toLocaleString() : "-"}
              <Typography
                component="span"
                sx={{ fontSize: 16, color: "#90A4AE", fontWeight: 700, mr: 1 }}
              >
                IQD
              </Typography>
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                mt: 1.5,
                px: 1.8,
                py: 0.5,
                borderRadius: 999,
                bgcolor: sc.bg,
                color: sc.color,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {sc.label}
            </Box>
          </Box>

          {/* Details */}
          <Box sx={{ px: 3, pb: 1 }}>
            <DetailRow label="رقم الطلب" value={`#${t.order_id ?? "-"}`} />
            <DetailRow
              label="النوع"
              value={`${t.e_payment_type ?? "-"} • ${
                t.from_app2app ? "App2App" : "Web"
              }`}
            />
            <DetailRow label="البوابة" value={t.gateway ?? "-"} />
            <DetailRow label="نقطة البيع" value={t.pos_name ?? "-"} />
            {t.status === "failed" && t.failed_details && (
              <DetailRow label="تفاصيل الفشل" value={t.failed_details} />
            )}
            <DetailRow
              label="التاريخ"
              value={
                t.created_at
                  ? dayjs(t.created_at).format("YYYY-MM-DD hh:mm a")
                  : "-"
              }
              last={!t.approval_date}
            />
            {t.approval_date && (
              <DetailRow
                label="تاريخ التأكيد"
                value={dayjs(t.approval_date).format("YYYY-MM-DD hh:mm a")}
                last
              />
            )}
          </Box>
          <Box sx={{ pb: 2 }} />
        </Box>
    </FormDialog>
  );
};

export default TransactionPrintDialog;
